import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { useSEO, SITE_URL } from "@/hooks/useSEO";

const methods = [
  { icon: "ri-bank-card-line", name: "Credit / Debit Card", desc: "Visa, Mastercard, Amex accepted worldwide", color: "bg-blue-50 text-blue-500" },
  { icon: "ri-paypal-line", name: "PayPal", desc: "Fast, secure PayPal checkout", color: "bg-sky-50 text-sky-500" },
  { icon: "ri-bank-line", name: "Wire Transfer", desc: "Direct bank transfer for large orders", color: "bg-green-50 text-green-500" },
  { icon: "ri-shield-check-line", name: "Trade Assurance Escrow", desc: "Funds held until delivery confirmed", color: "bg-amber-50 text-amber-500" },
  { icon: "ri-bit-coin-line", name: "Crypto (USDT/BTC)", desc: "Pay with major cryptocurrencies", color: "bg-orange-50 text-orange-500" },
  { icon: "ri-alipay-line", name: "Alipay / WeChat Pay", desc: "Popular Asian payment methods", color: "bg-red-50 text-red-500" },
];

const steps = [
  { step: "01", title: "Place Order", desc: "Select products and submit your purchase order to the supplier." },
  { step: "02", title: "Secure Payment", desc: "Pay via your preferred method. Funds are held in escrow." },
  { step: "03", title: "Production & Shipping", desc: "Supplier manufactures and ships your order with tracking." },
  { step: "04", title: "Confirm & Release", desc: "Inspect goods and release payment. Full refund if issues arise." },
];

export default function SecurePaymentPage() {
  useSEO({
    title: "Secure Payment Gateway | Trade Assurance Escrow | Crown Global",
    description: "Crown Global's secure payment gateway with trade assurance escrow. Accept Visa, Mastercard, PayPal, wire transfer, crypto, Alipay, and WeChat Pay. 100% buyer protection with 256-bit SSL encryption.",
    keywords: "secure payment, trade assurance, escrow payment, buyer protection, global payment methods, Crown Global payment",
    canonical: "/secure-payment",
    ogTitle: "Secure Payment Gateway — Crown Global",
    ogDescription: "Trade assurance escrow. 6 payment methods. 100% buyer protection. 256-bit SSL encryption.",
    ogImage: "https://public.readdy.ai/ai/img_res/cc005c75027f8f1e5556cdf861aa929a.jpg",
    schemaJson: {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "WebPage",
          "@id": `${SITE_URL}/secure-payment`,
          "url": `${SITE_URL}/secure-payment`,
          "name": "Secure Payment Gateway — Crown Global",
          "description": "Secure payment gateway with trade assurance escrow protecting every transaction on Crown Global.",
          "isPartOf": { "@id": `${SITE_URL}/#website` },
          "breadcrumb": {
            "@type": "BreadcrumbList",
            "itemListElement": [
              { "@type": "ListItem", "position": 1, "name": "Home", "item": SITE_URL },
              { "@type": "ListItem", "position": 2, "name": "Secure Payment", "item": `${SITE_URL}/secure-payment` }
            ]
          }
        },
        {
          "@type": "Service",
          "@id": `${SITE_URL}/secure-payment#service`,
          "url": `${SITE_URL}/secure-payment`,
          "name": "Crown Global Trade Assurance Escrow Payment",
          "description": "Secure escrow payment system protecting buyers and sellers. Funds held until delivery confirmed. 256-bit SSL encryption.",
          "provider": { "@id": `${SITE_URL}/#organization` },
          "areaServed": "Worldwide",
          "serviceType": "Payment Gateway",
          "offers": {
            "@type": "AggregateOffer",
            "priceCurrency": "USD",
            "description": "Visa, Mastercard, PayPal, Wire Transfer, Crypto, Alipay, WeChat Pay accepted"
          }
        }
      ]
    }
  });

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <div className="pt-32 pb-16">
        <div className="max-w-screen-2xl mx-auto px-4 md:px-8">
          {/* Hero */}
          <div className="relative rounded-2xl overflow-hidden mb-14">
            <img src="https://readdy.ai/api/search-image?query=secure%20digital%20payment%20technology%20global%20finance%20network%20abstract%20professional%20photography%20dramatic%20lighting%20modern%20business%20concept%20dark%20background&width=1400&height=380&seq=pay1&orientation=landscape" alt="Secure Payment" className="w-full h-64 md:h-80 object-cover object-top" />
            <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/50 to-transparent"></div>
            <div className="absolute inset-0 flex flex-col justify-center px-8 md:px-16">
              <div className="inline-flex items-center gap-2 bg-green-400/20 border border-green-400/40 text-green-300 text-xs font-semibold px-3 py-1.5 rounded-full mb-4 w-fit">
                <i className="ri-shield-check-line"></i> 100% Buyer Protection
              </div>
              <h1 className="text-3xl md:text-5xl font-extrabold text-white mb-3">Secure Payment Gateway</h1>
              <p className="text-white/80 text-base max-w-lg">Every transaction is protected by our escrow system. Pay with confidence — your money is safe until you confirm delivery.</p>
            </div>
          </div>

          {/* Payment Methods */}
          <div className="mb-14">
            <h2 className="text-2xl font-extrabold text-gray-900 mb-2">Accepted Payment Methods</h2>
            <p className="text-gray-500 text-sm mb-6">Choose from 6 secure payment options for global transactions</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {methods.map((m) => (
                <div key={m.name} className="bg-white rounded-xl p-5 flex items-start gap-4">
                  <div className={`w-12 h-12 flex items-center justify-center rounded-xl shrink-0 ${m.color}`}>
                    <i className={`${m.icon} text-2xl`}></i>
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-800 text-sm">{m.name}</h3>
                    <p className="text-gray-500 text-xs mt-0.5">{m.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* How it works */}
          <div className="bg-white rounded-2xl p-8 md:p-12 mb-14">
            <h2 className="text-2xl font-extrabold text-gray-900 mb-2 text-center">How Trade Assurance Works</h2>
            <p className="text-gray-500 text-sm text-center mb-10">Your payment is protected at every step</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {steps.map((s, i) => (
                <div key={s.step} className="relative text-center">
                  {i < steps.length - 1 && <div className="hidden lg:block absolute top-6 left-[60%] w-full h-0.5 bg-amber-100 z-0"></div>}
                  <div className="relative z-10 w-12 h-12 flex items-center justify-center bg-amber-400 text-white font-extrabold text-lg rounded-full mx-auto mb-4">{s.step}</div>
                  <h3 className="font-bold text-gray-800 text-sm mb-2">{s.title}</h3>
                  <p className="text-gray-500 text-xs leading-relaxed">{s.desc}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Trust badges */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { icon: "ri-lock-2-line", label: "256-bit SSL Encryption" },
              { icon: "ri-shield-check-line", label: "PCI DSS Compliant" },
              { icon: "ri-refund-2-line", label: "Full Refund Guarantee" },
              { icon: "ri-customer-service-2-line", label: "24/7 Dispute Support" },
            ].map((b) => (
              <div key={b.label} className="bg-white rounded-xl p-5 flex flex-col items-center text-center gap-3">
                <div className="w-12 h-12 flex items-center justify-center bg-green-50 rounded-full">
                  <i className={`${b.icon} text-green-500 text-2xl`}></i>
                </div>
                <span className="text-sm font-semibold text-gray-700">{b.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
